import { Sprite, Texture } from "pixi.js";
import type { DestroyOptions } from "pixi.js";

export class Video extends Sprite {
  public video: HTMLVideoElement | undefined;
  private _muted: boolean;
  private _autoplay: boolean;
  private _loop: boolean;

  private onPlayCallbacks: Array<(e: Event) => void> = [];
  private onPauseCallbacks: Array<(e: Event) => void> = [];
  private onEndedCallbacks: Array<(e: Event) => void> = [];

  public static fromAlias(
    alias: string,
    autoplay: boolean = false,
    muted: boolean = false,
    loop: boolean = false,
  ): Video {
    const texture = Texture.from(alias);
    return new Video({
      texture: texture,
      autoplay: autoplay,
      muted: muted,
      loop: loop,
    });
  }

  constructor({
    texture,
    autoplay = false,
    muted = false,
    loop = false,
  }: {
    texture: Texture;
    autoplay?: boolean;
    muted?: boolean;
    loop?: boolean;
  }) {
    super(texture);
    this._muted = muted;
    this._autoplay = autoplay;
    this._loop = loop;

    this.setTexture(texture);
  }

  public setTexture = (texture: Texture): void => {
    if (
      texture.source.resource === undefined ||
      !(texture.source.resource instanceof HTMLVideoElement)
    ) {
      throw new Error("Video texture source is not an HTMLVideoElement");
    }
    if (this.video) {
      this.video.removeEventListener("play", this.onPlayCallback);
      this.video.removeEventListener("pause", this.onPauseCallback);
      this.video.removeEventListener("ended", this.onEndedCallback);
    }

    this.texture = texture;
    this.video = this.texture.source.resource;

    if (this.video) {
      this.video.muted = this._muted;
      this.video.autoplay = this._autoplay;
      this.video.loop = this._loop;
      this.video.pause();
      this.video.currentTime = 0;

      this.video.addEventListener("play", this.onPlayCallback);
      this.video.addEventListener("pause", this.onPauseCallback);
      this.video.addEventListener("ended", this.onEndedCallback);
    }
  };

  public setTextureFromAlias = (alias: string): void => {
    const texture = Texture.from(alias);
    this.setTexture(texture);
  };

  public setTime = (time: number): void => {
    if (!this.video) {
      throw new Error("Video not initialized");
    }
    this.video.currentTime = time;
  };

  public play = async (time?: number): Promise<void> => {
    if (!this.video) {
      throw new Error("Video not initialized");
    }

    if (time !== undefined) {
      this.setTime(time);
    }

    return this.video.play();
  };

  public pause(): void {
    if (!this.video) {
      throw new Error("Video not initialized");
    }

    return this.video.pause();
  }

  public stop(): void {
    if (!this.video) {
      throw new Error("Video not initialized");
    }

    this.video.pause();
    this.video.currentTime = 0;
  }

  public isPlaying(): boolean {
    if (!this.video) {
      throw new Error("Video not initialized");
    }

    return !!(
      this.video.currentTime > 0 &&
      !this.video.paused &&
      !this.video.ended &&
      this.video.readyState > 2
    );
  }

  public isLoaded(): boolean {
    return true;
  }

  set loop(loop: boolean) {
    this._loop = loop;
    if (this.video) {
      this.video.loop = this._loop;
    }
  }

  get filename(): string {
    return this.video?.currentSrc || this.video?.src || "";
  }

  get loop(): boolean {
    return this._loop;
  }

  get paused(): boolean {
    return this.video?.paused || false;
  }

  get duration(): number {
    return this.video?.duration || 0;
  }

  get currentTime(): number {
    return this.video?.currentTime || 0;
  }

  get remaining(): number {
    return this.duration - this.currentTime;
  }

  get progress(): number {
    return this.duration ? this.currentTime / this.duration : 0;
  }

  get muted(): boolean {
    return this.video?.muted || false;
  }

  get playbackRate(): number {
    return this.video?.playbackRate || 1;
  }

  set playbackRate(rate: number) {
    if (!this.video) {
      throw new Error("Video not initialized");
    }

    this.video.playbackRate = rate;
  }

  set muted(muted: boolean) {
    if (!this.video) {
      return;
    }

    this.video.muted = muted;
  }

  set volume(volume: number) {
    if (!this.video) {
      return;
    }

    this.video.volume = Math.max(0, Math.min(1, volume));
  }

  onPlay = (fn: (e?: Event) => void) => {
    this.onPlayCallbacks.push(fn);
  };

  onPause = (fn: (e?: Event) => void) => {
    this.onPauseCallbacks.push(fn);
  };

  onEnded = (fn: (e?: Event) => void) => {
    this.onEndedCallbacks.push(fn);
  };

  onPlayCallback = (e: Event) => {
    this.onPlayCallbacks.forEach((fn) => fn(e));
  };

  onPauseCallback = (e: Event) => {
    this.onPauseCallbacks.forEach((fn) => fn(e));
  };

  onEndedCallback = (e: Event) => {
    this.onEndedCallbacks.forEach((fn) => fn(e));
  };

  destroy(options?: DestroyOptions): void {
    if (this.video) {
      this.video.removeEventListener("play", this.onPlayCallback);
      this.video.removeEventListener("pause", this.onPauseCallback);
      this.video.removeEventListener("ended", this.onEndedCallback);
      this.video.pause();
      this.video.src = "";
      this.video.load();
      this.video = undefined;
    }

    super.destroy(options);
  }
}
